import CheckIcon from '@mui/icons-material/Check';
import { Box, Typography, useTheme } from '@mui/material';
import { ReactNode } from 'react';

interface SectionShellProps {
  stepNumber: number;
  title: string;
  description?: string;
  isComplete: boolean;
  isLast?: boolean;
  children: ReactNode;
}

/**
 * The numbered-circle-to-green-tick affordance, its vertical connector and the
 * required-field marker — repeated by all four form sections, so it is extracted
 * once rather than copied four times (see the design doc's component breakdown).
 */
export const SectionShell = ({
  stepNumber,
  title,
  description,
  isComplete,
  isLast = false,
  children,
}: SectionShellProps) => {
  const theme = useTheme();

  return (
    <Box sx={{ display: 'flex', gap: theme.spacing(4) }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontWeight: 600,
            color: theme.palette.white.main,
            backgroundColor: isComplete ? theme.palette.success.main : theme.palette.primary.main,
          }}
        >
          {isComplete ? <CheckIcon fontSize="small" /> : stepNumber}
        </Box>
        {!isLast && (
          <Box sx={{ flex: 1, width: '2px', backgroundColor: theme.palette.divider, my: 1 }} />
        )}
      </Box>
      <Box sx={{ flex: 1, pb: isLast ? 0 : theme.spacing(6) }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {title}
          </Typography>
          <Typography component="span" sx={{ color: 'error.main' }}>
            *
          </Typography>
        </Box>
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: theme.spacing(3) }}>
            {description}
          </Typography>
        )}
        {children}
      </Box>
    </Box>
  );
};
